import * as React from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2, MessageSquareText, TriangleAlert } from 'lucide-react'
import {
  getApiKey,
  getOntologyApiBaseUrl,
  isIdentityEnabled,
  setApiKey,
  useConnectExternalMessagingTenantMutation,
  useGetExternalMessagingTenantQuery,
  type MessagingPlatform
} from '@axilon-apps/domains-ontology-ui-state'

import { Empty } from '../../components/primitives/Empty'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'

const PLATFORM_LABELS: Record<MessagingPlatform, string> = {
  teams: 'Microsoft Teams',
  slack: 'Slack'
}

const INTEGRATIONS_PATH = '/settings?tab=integrations'

const ALREADY_CONNECTED_REASONS = new Set([
  'EXTERNAL_MESSAGING_TENANT_ALREADY_EXISTS',
  'EXTERNAL_MESSAGING_TENANT_ALREADY_CONNECTED',
  'TENANT_ALREADY_CONNECTED'
])

type ParsedConnectParams =
  | { ok: true; tenantId: string; platform: MessagingPlatform }
  | { ok: false; title: string; hint: string }

export function parseMessagingConnectParams(searchParams: URLSearchParams): ParsedConnectParams {
  const tenantId = (searchParams.get('tenant_id') ?? '').trim()
  const platform = (searchParams.get('platform') ?? '').trim().toLowerCase()

  if (!tenantId) {
    return {
      ok: false,
      title: 'Missing tenant ID',
      hint: 'Open this page from the Slack or Microsoft Teams install link so tenant_id is present.'
    }
  }

  if (platform !== 'teams' && platform !== 'slack') {
    return {
      ok: false,
      title: 'Unsupported messaging platform',
      hint: 'The platform query parameter must be either "teams" or "slack".'
    }
  }

  return { ok: true, tenantId, platform }
}

function isAlreadyConnectedError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const status = 'status' in err ? (err as { status?: unknown }).status : undefined
  const data = 'data' in err ? (err as { data?: unknown }).data : undefined

  if (status !== 409 || !data || typeof data !== 'object') return false
  const reason = (data as { reason?: unknown }).reason
  return typeof reason === 'string' && ALREADY_CONNECTED_REASONS.has(reason)
}

function extractConnectError(err: unknown): string {
  if (isAlreadyConnectedError(err)) return ''
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: unknown }).data
    if (data && typeof data === 'object') {
      const envelope = data as { message?: unknown; details?: Record<string, unknown> }
      if (envelope.details) {
        const detail = Object.values(envelope.details).find(
          (value) => typeof value === 'string' && value.length > 0
        )
        if (typeof detail === 'string') return detail
      }
      if (typeof envelope.message === 'string' && envelope.message.length > 0) {
        return envelope.message
      }
    }
  }
  return 'Could not finish the messaging bot connection.'
}

function successPath(params: {
  platform: MessagingPlatform
  tenantId: string
  status: 'connected' | 'already_connected'
  tenantName?: string
}): string {
  const next = new URLSearchParams({
    platform: params.platform,
    tenant_id: params.tenantId,
    status: params.status
  })
  if (params.tenantName) next.set('tenant_name', params.tenantName)
  return `/integrations/connect/success?${next.toString()}`
}

export default function MessagingConnectPage(): React.JSX.Element {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const endpoint = getOntologyApiBaseUrl()
  const parsed = React.useMemo(() => parseMessagingConnectParams(searchParams), [searchParams])
  const [error, setError] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [slackSigningKey, setSlackSigningKey] = React.useState('')
  const [connectTenant, connectState] = useConnectExternalMessagingTenantMutation()
  const isWorking = isSubmitting || connectState.isLoading

  const requiresHmacKey = parsed.ok && parsed.platform !== 'teams'
  const storedApiKey = getApiKey().trim()
  const slackSigningKeyInput = slackSigningKey.trim()
  const resolvedHmacKey = storedApiKey || slackSigningKeyInput

  // A returning user who clicks the bot's login link after the tenant is
  // already whitelisted should not see the permission prompt again — send them
  // straight to the Integrations page. The lookup needs the stored HMAC key, so
  // skip it (and fall back to the prompt) when no key is configured.
  const hasApiKey = storedApiKey.length > 0
  const whitelistQuery = useGetExternalMessagingTenantQuery(
    { external_tenant_id: parsed.ok ? parsed.tenantId : '' },
    { skip: !parsed.ok || !hasApiKey }
  )
  const alreadyWhitelisted = Boolean(whitelistQuery.data)
  const isCheckingWhitelist = parsed.ok && hasApiKey && whitelistQuery.isLoading

  const validateHmacKey = React.useCallback(
    async (trimmedKey: string): Promise<void> => {
      const response = await fetch(`${endpoint}/health`, {
        headers: { 'X-API-Key': trimmedKey }
      })
      if (response.ok) return
      if (response.status === 401) {
        throw new Error('Authentication failed. Check the HMAC API key and try again.')
      }
      throw new Error(`Proxy authentication check returned ${response.status}.`)
    },
    [endpoint]
  )

  const handleAccept = async () => {
    if (!parsed.ok || isWorking) return

    // Microsoft Teams is whitelisted using the connecting admin's verified
    // Axilon identity JWT (attached by ontologyBaseQuery): it authenticates the
    // connect AND supplies the organization, so no signing key is needed. Other
    // platforms still pass an HMAC signing key the proxy stores as a write-only
    // secret.
    if (requiresHmacKey && !resolvedHmacKey) {
      setError('A valid HMAC API key is required before connecting the bot.')
      return
    }

    setError('')
    setIsSubmitting(true)
    try {
      if (requiresHmacKey) {
        await validateHmacKey(resolvedHmacKey)
        if (!storedApiKey && slackSigningKeyInput) {
          setApiKey(slackSigningKeyInput)
        }
      }
      const result = await connectTenant({
        external_tenant_id: parsed.tenantId,
        platform: parsed.platform,
        ...(requiresHmacKey && resolvedHmacKey ? { hmac_key: resolvedHmacKey } : {})
      }).unwrap()

      navigate(
        successPath({
          platform: parsed.platform,
          tenantId: parsed.tenantId,
          status: result.status === 'already_connected' ? 'already_connected' : 'connected',
          tenantName: result.tenant?.external_tenant_name
        }),
        { replace: true }
      )
    } catch (err) {
      if (isAlreadyConnectedError(err)) {
        navigate(
          successPath({
            platform: parsed.platform,
            tenantId: parsed.tenantId,
            status: 'already_connected'
          }),
          { replace: true }
        )
        return
      }
      setError(err instanceof Error ? err.message : extractConnectError(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDecline = () => {
    navigate('/plant', { replace: true })
  }

  if (!parsed.ok) {
    return (
      <main className="min-h-screen bg-bg px-4 py-16">
        <div className="mx-auto max-w-xl rounded-xl border border-line bg-bg-elev shadow-sm">
          <Empty
            icon={TriangleAlert}
            title={parsed.title}
            hint={parsed.hint}
            className="px-8 py-12"
          />
        </div>
      </main>
    )
  }

  if (alreadyWhitelisted) {
    return <Navigate to={INTEGRATIONS_PATH} replace />
  }

  if (isCheckingWhitelist) {
    return (
      <main className="min-h-screen bg-bg px-4 py-16 text-ink">
        <Card className="mx-auto max-w-xl">
          <CardContent className="flex flex-col items-center gap-4 px-8 py-12 text-center">
            <Loader2 className="size-6 animate-spin text-brand" />
            <p className="text-sm text-ink-3">Checking your connection…</p>
          </CardContent>
        </Card>
      </main>
    )
  }

  const platformLabel = PLATFORM_LABELS[parsed.platform]

  return (
    <main className="min-h-screen bg-bg px-4 py-16 text-ink">
      <Card className="mx-auto max-w-xl">
        <CardContent className="flex flex-col items-center gap-6 px-8 py-10 text-center">
          <div className="rounded-xl bg-brand-soft p-3 text-brand">
            <MessageSquareText size={26} />
          </div>

          <h1 className="text-2xl font-semibold">
            Do you give permission to connect {platformLabel} to your Axilon Prophet Organization?
          </h1>

          {error ? (
            <div
              role="alert"
              className="w-full rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down"
            >
              {error}
            </div>
          ) : null}

          {requiresHmacKey && !storedApiKey ? (
            <div className="grid w-full gap-1.5 text-left">
              <Label htmlFor="messaging-slack-signing-key">Slack signing key</Label>
              <Input
                id="messaging-slack-signing-key"
                type="password"
                placeholder="Paste your HMAC API key"
                value={slackSigningKey}
                onChange={(event) => setSlackSigningKey(event.target.value)}
                className="font-mono"
              />
              <p className="text-xs text-ink-3">
                {isIdentityEnabled()
                  ? 'Slack bot webhooks still need a signing key. It is stored locally on this device after a successful connection.'
                  : 'Paste the same HMAC API key you configured in Settings, or enter it here.'}
              </p>
            </div>
          ) : null}

          <div className="flex flex-wrap justify-center gap-2">
            <Button onClick={() => void handleAccept()} disabled={isWorking}>
              {isWorking ? <Loader2 className="animate-spin" /> : null}
              Yes
            </Button>
            <Button type="button" variant="outline" disabled={isWorking} onClick={handleDecline}>
              No
            </Button>
          </div>
        </CardContent>
      </Card>
    </main>
  )
}
